import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    firstValueFrom,
    Observable,
    Subscription,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from 'ngx-alerts';
import { WithdrawService } from './withdraw.service';
import { WithdrawLimitsService } from '../withdraw-limits/withdraw-limits.service';
import { WithdrawModalEndTransactionComponent } from '../../shared/withdraw-modal-end-transaction/withdraw-modal-end-transaction.component';
import { ModalResultInfoComponent } from '../../shared/modal-result-info/modal-result-info.component';
import { ConfirmWithdrawDto } from './confirmWithdraw.dto';
import { WithdrawModalEndDto } from '../../shared/withdraw-modal-end-transaction/withdraw-modal-end.dto';

@Injectable()
export class ConfirmTransactionService {
    private dialogSub: Subscription;
    private confirmTransactionSub: Subscription;
    private isSubmittedSubject = new BehaviorSubject<boolean>(true);
    public isSubmitted$: Observable<boolean> =
        this.isSubmittedSubject.asObservable();

    constructor(
        private dialog: MatDialog,
        private withdrawService: WithdrawService,
        private withdrawLimitsService: WithdrawLimitsService,
        private alertService: AlertService
    ) {}

    async confirmWithdrawTransaction(data: any): Promise<void> {
        const { paymentSystem, metadata, withdrawId } = data;
        const body: ConfirmWithdrawDto = { ...data };

        this.isSubmittedSubject.next(true);
        const dialogRef = this.dialog.open(
            WithdrawModalEndTransactionComponent,
            {
                data: {
                    paymentSystem: paymentSystem || metadata?.paymentSystem,
                    id: withdrawId,
                    message: 'Confirm',
                    color: 'green',
                    metadata: { ...metadata },
                },
            }
        );

        this.dialogSub = dialogRef.afterClosed().subscribe(async (result) => {
            this.unsubscribeDialogSub();
            if (!result || !result.isSubmitted) {
                this.isSubmittedSubject.next(false);
                return;
            }
            if (!(await this.withdrawLimitCheck(body))) {
                this.isSubmittedSubject.next(false);
                return;
            }

            this.handleConfirmTransaction(body, result);
            this.isSubmittedSubject.next(true);
        });
    }

    async withdrawLimitCheck(body: ConfirmWithdrawDto): Promise<boolean> {
        const amount: number = parseFloat(body.amount);
        try {
            const unit: any = await firstValueFrom(
                this.withdrawLimitsService.getLimitsByPU(body.paymentSystem)
            );

            const limit: number = parseFloat(unit?.limit);

            if (
                amount > limit ||
                amount > parseFloat(unit?.singleTransactionLimit)
            ) {
                this.alertService.warning(
                    `Amount Exceeds ${amount > limit ? '' : 'single '} limit`
                );
                return false;
            }
        } catch (error) {
            console.error(error.error.text);
        }

        return true;
    }

    private updateConfirmWithdrawBodyFromModalData(
        body: ConfirmWithdrawDto,
        modalData: WithdrawModalEndDto
    ): ConfirmWithdrawDto {
        if (modalData.bankTransfer) {
            body.paymentSystem = modalData.data;
            body.metadata.paymentSystem = modalData.data;
        }

        if (modalData.fieldName) {
            body[modalData.fieldName] = modalData.data;
            body.metadata[modalData.fieldName] = modalData.data;
        }
        body.operator = modalData.operator;
        return body;
    }

    private openModalInfoWindow(status: string, message: string): void {
        this.dialog.open(ModalResultInfoComponent, {
            data: {
                status: status,
                message: message,
                error: status === 'error',
            },
        });
        this.confirmTransactionSub.unsubscribe();
        this.dialogSub.unsubscribe();
    }

    private handleConfirmTransaction(
        body: ConfirmWithdrawDto,
        result: WithdrawModalEndDto
    ): void {
        body = this.updateConfirmWithdrawBodyFromModalData(body, result);

        this.confirmTransactionSub = this.withdrawService
            .confirmtransaction(body)
            .subscribe((response: any) => {
                if (!response?.message) {
                    response = {
                        status: 'error',
                        message:
                            'Transaction failed or did not return any message',
                    };
                }
                this.openModalInfoWindow(response?.status, response?.message);
            });
    }

    private unsubscribeDialogSub(): void {
        if (this.dialogSub) {
            this.dialogSub.unsubscribe();
        }
    }
}
